import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { SITE } from "../data/site";

// Auf dem Handy und bei reduzierter Bewegung werden Einblend-Animationen
// abgeschwächt (Performance/Core Web Vitals, INP).
const REDUCE_MOTION = typeof window !== "undefined"
  && (window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.matchMedia("(max-width: 767px)").matches);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={REDUCE_MOTION ? { opacity: 0 } : { opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={REDUCE_MOTION ? { duration: 0.25, delay: 0 } : { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const SectionLabel = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-ember">
    <span className="h-px w-8 bg-gradient-to-r from-ember to-signal" />
    {children}
  </span>
);

export const CtaBlock = ({ title = "Schaden melden – wir kümmern uns", text = "Schildern Sie uns kurz Ihren Fall. Wir melden uns umgehend und vereinbaren einen Termin direkt bei Ihnen vor Ort." }) => (
  <Reveal>
    <div data-testid="cta-block" className="ci-gradient grain relative rounded-2xl p-8 sm:p-10 text-white overflow-hidden">
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold">{title}</h2>
          <p className="mt-2 text-slate-200 text-sm sm:text-base max-w-xl">{text}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <a href={SITE.phoneHref} data-testid="cta-block-phone-button" className="flex items-center justify-center gap-2 bg-white text-navy font-bold px-6 py-3.5 rounded-full hover:bg-slate-100 transition-colors">
            <Phone size={18} /> {SITE.phone}
          </a>
          <Link to="/schaden-melden-formular" data-testid="cta-block-form-button" className="cta-gradient flex items-center justify-center gap-2 font-bold px-6 py-3.5 rounded-full text-white">
            Schaden melden <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  </Reveal>
);

export const TeamCard = () => (
  <div data-testid="team-card" className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-lg">
    <img src="/team/team-gruppe.webp" alt="Das Team von UnfallExpert24: Denis Sieger, Giovanni Timperio und Sezgin Cömlek – TÜV-zertifizierte KFZ-Sachverständige" className="w-full h-56 object-cover object-top" loading="lazy" />
    <div className="p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-navy">Ihr Gutachter-Team</p>
      <p className="text-[11px] text-slate-500 mt-1 leading-snug">Denis, Giovanni & Sezgin – persönlich für Sie vor Ort in ganz NRW.</p>
      <a href="/ueber-uns/" className="inline-flex items-center gap-1.5 text-xs font-bold text-ember mt-2 hover:text-navy transition-colors">Team kennenlernen <ArrowRight size={12} /></a>
    </div>
  </div>
);

export const TrustBlock = ({ showReviews = true }) => (
  <div data-testid="trust-block" className="bg-white border border-slate-200 rounded-2xl p-6 shadow-lg space-y-5">
    <div className="flex items-center gap-4">
      <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland Prüfzeichen – Geprüfte Qualifikation, ID 0000046955" width="440" height="162" className="h-16 w-auto" loading="lazy" />
      <div>
        <p className="font-display font-bold text-navy">TÜV Rheinland zertifiziert</p>
        <p className="text-xs text-slate-500">KFZ-Sachverständiger für Schäden und Bewertung</p>
      </div>
    </div>
    {showReviews && (
      <div data-testid="trust-block-reviews" className="bg-slate-50 rounded-xl p-4">
        {/* BITTE_EINTRAGEN: Elfsight-Widget-Code für Google-Bewertungen hier einbinden (App-ID aus Elfsight-Dashboard) */}
        <p className="text-sm text-slate-600">Unsere aktuellen Google-Bewertungen laden wir hier über das Elfsight-Widget (live, nicht hart codiert).</p>
      </div>
    )}
    <div className="flex items-center gap-4">
      <img src="/team/team-gruppe.webp" alt="Das Gutachter-Team von UnfallExpert24: Denis Sieger, Giovanni Timperio und Sezgin Cömlek" className="w-24 h-24 rounded-xl object-cover object-top" loading="lazy" />
      <ul className="text-sm text-slate-700 space-y-1.5">
        <li className="flex items-start gap-1.5"><CheckCircle2 size={15} className="text-ember shrink-0 mt-0.5" /> Wir kommen zu Ihnen – auch am Abend bis 22 Uhr</li>
        <li className="flex items-start gap-1.5"><CheckCircle2 size={15} className="text-ember shrink-0 mt-0.5" /> Direkte Abwicklung mit der Versicherung</li>
        <li className="flex items-start gap-1.5"><CheckCircle2 size={15} className="text-ember shrink-0 mt-0.5" /> Kostenlos bei unverschuldetem Unfall</li>
      </ul>
    </div>
  </div>
);

export const TuvMark = ({ compact }) => (
  <div data-testid="tuv-pruefzeichen" className={`bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 ${compact ? "" : "shadow-lg"}`}>
    <img src="/tuv-pruefzeichen-440.webp" alt="TÜV Rheinland Prüfzeichen – Geprüfte Qualifikation, gültig bis 15.11.2026, ID 0000046955" width="440" height="162" className={compact ? "h-14 w-auto" : "h-20 w-auto"} loading="lazy" />
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-navy">TÜV Rheinland zertifiziert</p>
      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">Geprüfte Qualifikation · Prüf-ID 0000046955 · gültig bis 15.11.2026</p>
    </div>
  </div>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-slate-400 mb-4" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden="true">/</span>}
          {item.path ? (
            <Link to={item.path} className="hover:text-ember transition-colors">{item.name}</Link>
          ) : (
            <span className="text-slate-200 font-medium" aria-current="page">{item.name}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);
